const DATE_ONLY_TIMEZONE_ID = 'UTC';
const APP_LOCALE = 'en-GB';

/** IANA zone used for timestamps (`includeTime`). Date-only values always stay on UTC. */
let displayTimeZoneId = 'UTC';

export function getDisplayTimeZone(): string {
  return displayTimeZoneId;
}

/** Sets the IANA zone for on-screen timestamps. Invalid or blank values fall back to UTC. */
export function setDisplayTimeZone(ianaTimeZoneId: string | null | undefined): void {
  const trimmed = ianaTimeZoneId?.trim();
  displayTimeZoneId = trimmed || 'UTC';
}

function timeZoneOrUtc(timeZoneId: string): string {
  try {
    new Intl.DateTimeFormat('en', { timeZone: timeZoneId }).format(new Date());
    return timeZoneId;
  } catch {
    return 'UTC';
  }
}

/**
 * Parse a date value as UTC, treating bare ISO datetime strings (no Z or offset,
 * e.g. "2026-05-15T00:00:00") as UTC midnight rather than local time.
 * Date-only strings ("2026-05-15") are already treated as UTC midnight by the spec.
 *
 * Use this instead of `new Date(iso)` everywhere a server-returned ISO string is
 * consumed for sorting, arithmetic, or date-picker initialisation.
 *
 * Returns null for null / undefined / empty / invalid inputs.
 */
export function parseUtcDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  let normalized = value;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value)) {
    // Bare datetime string — no Z or offset — append Z so browsers treat it as UTC.
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.warn(
        `[parseUtcDate] Date string is missing timezone info: "${value}". ` +
          'Treated as UTC. Check that StoresUtcInstant() is applied to the corresponding EF Core entity property.',
      );
    }
    normalized = value + 'Z';
  }
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * UTC milliseconds for sort comparisons. Returns 0 for null / invalid.
 * Use in place of `new Date(iso).getTime()` for server-returned date fields.
 */
export function utcMs(value: string | Date | null | undefined): number {
  return parseUtcDate(value)?.getTime() ?? 0;
}

/**
 * Calendar date as yyyy-MM-dd in UTC.
 */
export function formatCalendarDateIso(date: string | Date | null | undefined): string {
  if (date == null || date === '') return '';
  const dateObj = parseUtcDate(date);
  if (!dateObj) return '';
  const parts = new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: DATE_ONLY_TIMEZONE_ID,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(dateObj);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;
  if (!y || !m || !d) return '';
  return `${y}-${m}-${d}`;
}

/**
 * True when today's UTC calendar date is strictly after the given deadline
 * (inclusive deadlines: returns false on the deadline date itself).
 */
export function isUtcCalendarDatePassed(deadline: string | Date | null | undefined): boolean {
  const deadlineIso = formatCalendarDateIso(deadline);
  if (!deadlineIso) return false;
  const todayIso = formatCalendarDateIso(new Date());
  return todayIso > deadlineIso;
}

/** App standard for date-only display (e.g. "13 March 2026"). Use formatDateStandard(date) or formatDate(date, STANDARD_DATE_FORMAT). */
export const STANDARD_DATE_FORMAT = { format: 'long' as const } as const;

function calendarYearInAppTz(date: Date): number {
  return parseInt(
    date.toLocaleString(APP_LOCALE, { timeZone: DATE_ONLY_TIMEZONE_ID, year: 'numeric' }),
    10
  );
}

/**
 * Formats a date string or Date object to a user-friendly format.
 * Default format is the app standard ("13 March 2026"); pass options to override.
 * @param date - Date string or Date object
 * @param options - Formatting options (omit for standard format)
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date | null | undefined,
  options?: {
    includeTime?: boolean;
    format?: 'long' | 'short' | 'medium';
  }
): string {
  if (!date) return '';

  const dateObj = parseUtcDate(date);
  if (!dateObj) return '';

  const format = options?.format ?? 'long';
  const includeTime = options?.includeTime || false;

  if (includeTime) {
    const zone = timeZoneOrUtc(displayTimeZoneId);
    return dateObj.toLocaleString(APP_LOCALE, {
      timeZone: zone,
      year: 'numeric',
      month: format === 'long' ? 'long' : format === 'medium' ? 'short' : 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    });
  }

  // Date only formats — always UTC so midnight-UTC calendar days do not shift
  switch (format) {
    case 'long':
      return dateObj.toLocaleDateString(APP_LOCALE, {
        timeZone: DATE_ONLY_TIMEZONE_ID,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    case 'medium':
      return dateObj.toLocaleDateString(APP_LOCALE, {
        timeZone: DATE_ONLY_TIMEZONE_ID,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    case 'short':
      return dateObj.toLocaleDateString(APP_LOCALE, {
        timeZone: DATE_ONLY_TIMEZONE_ID,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      });
    default:
      return dateObj.toLocaleDateString(APP_LOCALE, {
        timeZone: DATE_ONLY_TIMEZONE_ID,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
  }
}

/** Formats a date using the app standard (e.g. "13 March 2026"). Use this for consistent date display across the app. */
export function formatDateStandard(date: string | Date | null | undefined): string {
  return formatDate(date, STANDARD_DATE_FORMAT);
}

/**
 * Formats a date range (e.g., "Apr 1 - May 31, 2026")
 */
export function formatDateRange(startDate: string | Date, endDate: string | Date): string {
  const start = parseUtcDate(startDate);
  const end = parseUtcDate(endDate);

  if (!start || !end) {
    return '';
  }

  const startFormatted = formatDate(start, { format: 'medium' });
  const endFormatted = formatDate(end, { format: 'medium' });

  // If same calendar year in app timezone, only show year once
  if (calendarYearInAppTz(start) === calendarYearInAppTz(end)) {
    return `${startFormatted} - ${endFormatted}`;
  }

  return `${startFormatted} - ${endFormatted}`;
}






